// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { extractLocale } from './locale-extraction.js';
/**
 * Does this per-scheme entry carry a level somebody actually screened?
 *
 * fix-sprint-20260904-2120 F22. Named rather than inlined because two branches
 * below ask the question and they must agree: a subject counted as screened
 * under N schemes and a subject reported `scheme_scoped_only` are the same
 * subject, and the register export renders the second as an assessment.
 *
 * `manual_legacy` is deliberately NOT approved. Its own docblock at
 * `CLASSIFICATION_TIER_SOURCES` says a value adopted from the 46.1.0 dropdown
 * "must not claim that value was screened" — this is where that stops being a
 * comment. An entry with a code and no source is likewise not approved: a
 * missing source is a data defect, and the safe reading of a defect is "not
 * screened", never "screened".
 */
function isApprovedEntry(entry) {
    return (typeof entry?.current_tier_code === 'string' &&
        entry.current_tier_code.length > 0 &&
        entry.current_tier_source === 'assessed_and_approved');
}
/**
 * Resolve a subject's tier from its classification profile, falling back to the
 * legacy mirrored field **by name**.
 *
 * The fallback is a named branch rather than a `??` tail on purpose: a caller
 * reading `is_legacy_fallback` can say so on screen, and a caller that forgets
 * still gets a correct tier rather than a silently wrong provenance.
 */
export function resolveClassifiedTier(profile, legacyDataTier, 
/**
 * Scheme id → the draft-mode facts of its declaring framework (P2-2).
 *
 * Optional, and its absence is reported rather than guessed: a caller that
 * supplies no map gets `duties_owed: null`, which reads as *not known here*,
 * never as `true`. Defaulting it to `true` would be the `null`-falls-through-
 * to-binding shape this stream keeps finding.
 */
schemeFacts) {
    const legacy = typeof legacyDataTier === 'string' && legacyDataTier ? legacyDataTier : null;
    /** The three P2-2 fields, for a branch where no single scheme answers. */
    const noScheme = {
        scheme_id: null,
        duties_owed: null,
        draft_warning: null,
        framework_status: null,
    };
    if (!profile) {
        return {
            tier_code: legacy,
            source: legacy ? 'legacy_platform_field' : null,
            is_legacy_fallback: legacy !== null,
            has_profile: false,
            classification_count: 0,
            ...noScheme,
        };
    }
    // Sprint 62.20.0 — how many schemes actually answer for this subject. The
    // legacy pair below mirrors at most ONE of them, so it cannot be the count.
    //
    // fix-sprint-20260904-2120 F22 — "answer" means an APPROVED answer. See
    // `isApprovedEntry`: a `manual_legacy` entry has a code and nobody screened
    // it, and counting it here is what let the export call it an assessment.
    const classificationCount = Array.isArray(profile.classifications)
        ? profile.classifications.filter((c) => isApprovedEntry(c ?? {})).length
        : 0;
    const code = typeof profile.current_tier_code === 'string' && profile.current_tier_code
        ? profile.current_tier_code
        : null;
    if (code) {
        // P2-2. This is the ONE branch that returns a scheme's own tier code, so it
        // is the one branch that can name the ladder the code belongs to. A scheme
        // the caller supplied no facts for reports `duties_owed: null` — unknown,
        // and rendered as unknown — rather than being assumed binding.
        const schemeId = typeof profile.scheme_id === 'string' && profile.scheme_id ? profile.scheme_id : null;
        const facts = schemeId ? (schemeFacts?.get(schemeId) ?? null) : null;
        return {
            tier_code: code,
            // A profile that carries a tier but no source is a data defect, not a
            // legacy read — reported as such rather than relabelled.
            source: profile.current_tier_source ?? null,
            is_legacy_fallback: false,
            has_profile: true,
            classification_count: classificationCount,
            scheme_id: schemeId,
            duties_owed: facts ? facts.duties_owed : null,
            draft_warning: facts ? facts.draft_warning : null,
            framework_status: facts ? (facts.framework_status ?? null) : null,
        };
    }
    // Sprint 62.20.0. The subject is screened — under one scheme or several —
    // and no scheme among them declares a `platform_data_tier`, so the mirror is
    // empty and `code` above was null. Without this branch such a subject falls
    // through to the legacy branch and is reported as unscreened, which is the
    // opposite of the truth and reaches the regulator-facing export that way.
    //
    // `tier_code` stays null deliberately. Naming one entry's code would present
    // one statutory ladder's level as though it were the subject's tier, and
    // which ladder a reader has in mind changes what that level means — the
    // merged label this sprint exists to prevent. The count is the honest
    // answer; the levels themselves are on the subject.
    if (classificationCount > 0) {
        return {
            tier_code: null,
            source: 'scheme_scoped_only',
            is_legacy_fallback: false,
            has_profile: true,
            classification_count: classificationCount,
            // No code is returned, so there is no ladder to name and no single
            // framework whose draft status could describe it. Naming one of several
            // here would be the merged label by another route.
            ...noScheme,
        };
    }
    // A profile exists and names no tier. The subject has been brought into the
    // classification module and not yet screened, which is NOT the same as never
    // having been classified — `has_profile` keeps them apart.
    //
    // Gate E12.5, Backend, P1-1. This branch first read
    // `source: legacy ? 'legacy_platform_field' : null`, dropping the profile's
    // own source — and that is not an edge case, it is the ONLY branch a
    // `manual_legacy` profile can ever reach. `createProfile` writes
    // `{ current_tier_code: null, current_tier_source: legacyTier ?
    // 'manual_legacy' : null }` (`classification-service.ts:330`), and
    // `current_tier_code` is written non-null in exactly one place — the approval
    // at `:1277`, always with `'assessed_and_approved'`. So the docblock table
    // above described a state the product cannot produce, while the state it does
    // produce was reported as if the subject had no profile at all. The registry
    // export emits `classified_tier.source` alone, so an adopted legacy tier and
    // a never-classified one rendered identically in a regulator-facing column.
    //
    // The two facts are separate and both are kept: `source` is the profile's own
    // provenance, `is_legacy_fallback` says the *code* came from the mirror.
    return {
        tier_code: legacy,
        source: profile.current_tier_source ?? (legacy ? 'legacy_platform_field' : null),
        is_legacy_fallback: legacy !== null,
        has_profile: true,
        classification_count: classificationCount,
        // The code here is the platform's own mirrored field, which belongs to no
        // scheme's ladder. `profile.scheme_id` may well be set — a selection with
        // nothing decided under it — and naming it would attribute a legacy value
        // to a scheme that never produced it.
        ...noScheme,
    };
}
/**
 * Lower-case the language subtag and leave the rest of the tag alone.
 *
 * Gate E12.5 (Sprint 64.72.0), F7. `resolveBundle` in
 * `registry-export-renderer.ts` normalises casing before it looks a bundle up;
 * this function did not, and `extractLocale` matches map keys exactly. So
 * `?locale=VI` resolved Vietnamese column headers — via `resolveBundle` — beside
 * an English tier title, because `map['VI']` misses, `'VI'` has no `-` so the
 * language-prefix step never runs, and the chain falls to `en`. A
 * mixed-language sheet, which is the artefact the sprint exists to prevent.
 *
 * Only the language subtag is touched. Uppercasing the region would be
 * lossless for `vi-vn`, but a three-subtag tag (`zh-Hant-TW`) would come back
 * as `zh-HANT`, and a pack that keys its title map on the full tag would then
 * miss a key it does have. Lowering the first subtag fixes the measured defect
 * and cannot invent one: `extractLocale` already bridges `vi-vn` → `vi`.
 */
function canonicalLanguageSubtag(locale) {
    const raw = (locale ?? '').trim();
    const dash = raw.indexOf('-');
    if (dash === -1)
        return raw.toLowerCase();
    return raw.slice(0, dash).toLowerCase() + raw.slice(dash);
}
/**
 * The label a reader should see for a resolved tier, in their own language.
 *
 * AC3: tier titles come from the scheme's own `tiers[].title`, so a German
 * tenant reads German tiers and a Vietnamese tenant reads *Dữ liệu cốt lõi*.
 * Returns `null` when the scheme does not declare the tier — which happens
 * legitimately on a legacy fallback, where the code is a platform value the
 * scheme never had.
 */
export function resolveTierTitle(tiers, tierCode, locale) {
    if (!tiers || !tierCode)
        return null;
    const tier = tiers.find((t) => t.code === tierCode);
    if (!tier)
        return null;
    const title = extractLocale(tier.title, canonicalLanguageSubtag(locale)).trim();
    return title || null;
}
//# sourceMappingURL=classified-tier.js.map